import {
  ChevronDown,
  ChevronRight,
  FileVideo,
  Image as ImageIcon,
  Layers,
  Sparkles,
} from 'lucide-react';

import type { AssetType } from '../../constants/assetTypes';
import { AssetGrid } from '../grid/AssetGrid';
import type { AssetUrlLookup } from '../grid/useAssetUrls';
import type { OlaTokenLookup } from '../grid/useOlaTokens';
import type { AssetTypeGroup } from './assetHierarchy';

interface AssetTypeSectionProps {
  typeGroup: AssetTypeGroup;
  nameplateKey: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  urls: AssetUrlLookup;
  olaTokens: OlaTokenLookup;
  isSelected: (id: string) => boolean;
  isSelectable?: (id: string) => boolean;
  onSelect: (id: string, modifiers: { shift?: boolean; meta?: boolean }) => void;
  onOpen: (id: string) => void;
  eligibleIds?: Set<string> | null;
  onToggleBasket?: (id: string) => void;
  basketIds?: Set<string>;
  expiredIds?: Set<string>;
  expiringSoonIds?: Set<string>;
}

function getAssetTypeIcon(assetType: AssetType) {
  switch (assetType) {
    case 'source_image':
      return <Sparkles className="h-3.5 w-3.5 text-[#0014cc]" />;
    case 'master_image':
      return <ImageIcon className="h-3.5 w-3.5 text-blue-600" />;
    case 'filmic':
      return <FileVideo className="h-3.5 w-3.5 text-indigo-600" />;
    case 'ola':
      return <Layers className="h-3.5 w-3.5 text-slate-500" />;
  }
}

export function AssetTypeSection({
  typeGroup,
  nameplateKey,
  isCollapsed,
  onToggleCollapse,
  urls,
  olaTokens,
  isSelected,
  isSelectable,
  onSelect,
  onOpen,
  eligibleIds,
  onToggleBasket,
  basketIds,
  expiredIds,
  expiringSoonIds,
}: AssetTypeSectionProps) {
  const sectionId = `type-section-${encodeURIComponent(nameplateKey)}-${encodeURIComponent(
    typeGroup.assetType,
  )}`;

  return (
    <div className="flex flex-col gap-2">
      {/* Clean lightweight asset-type sub-header */}
      <button
        type="button"
        onClick={onToggleCollapse}
        aria-expanded={!isCollapsed}
        aria-controls={sectionId}
        className="group flex items-center justify-between py-1 text-left"
      >
        <div className="flex items-center gap-2">
          {getAssetTypeIcon(typeGroup.assetType)}
          <h4 className="text-xs font-semibold text-slate-700 group-hover:text-slate-900">
            {typeGroup.label}
          </h4>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
            {typeGroup.assets.length}
          </span>
        </div>
        <span className="text-slate-400 group-hover:text-slate-600">
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </span>
      </button>

      {!isCollapsed && (
        <div id={sectionId}>
          <AssetGrid
            assets={typeGroup.assets}
            urls={urls}
            olaTokens={olaTokens}
            isSelected={isSelected}
            isSelectable={isSelectable}
            onSelect={onSelect}
            onOpen={onOpen}
            eligibleIds={eligibleIds}
            onToggleBasket={onToggleBasket}
            basketIds={basketIds}
            expiredIds={expiredIds}
            expiringSoonIds={expiringSoonIds}
            // A multi-nameplate asset renders in every one of its sections;
            // scoping the card ids by section keeps them unique in the DOM.
            domScope={`${encodeURIComponent(nameplateKey)}-`}
          />
        </div>
      )}
    </div>
  );
}
